/**
 * AppShell - Main layout component with sidebar navigation
 * Provides consistent layout across all authenticated pages
 * 
 * TIER GATING: All features visible to all tiers, but locked features
 * show a lock icon and upgrade prompt when clicked
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PaymentStatusBanner } from "./PaymentStatusBanner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTeamMessages } from "@/contexts/TeamMessageContext";
import FeaturesGuideDrawer from "@/components/FeaturesGuideDrawer";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { TIER_INFO, FEATURE_NAMES } from "@/config/tierFeatures";
import { getRoleDisplayName } from "@/utils/roleDisplayNames";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Users, Calendar, FileText, MessageSquare, Megaphone,
  BarChart3, Settings, LogOut, Menu, X, ChevronLeft, ChevronDown, ChevronRight,
  CreditCard, Utensils, UtensilsCrossed, Shield, Database, Upload, Phone, Mail,
  ClipboardList, UserCog, ArrowUpCircle, Building2, Globe, Home, Bell, Tag, FileCheck, Baby, FolderOpen, ShieldCheck, TrendingUp, Wrench, RefreshCw, Lock, Sparkles, BookOpen, Table, Printer, QrCode, HelpCircle, ScrollText, CheckCircle, Wand2
} from "lucide-react";
import HolidayCard from "./HolidayCard";
import StaffCalendar from "./StaffCalendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Main Navigation Items (Sidebar)
// requiredTier: 'basic' | 'premium' | 'deluxe' | 'enterprise' | null (null = always accessible)
// All items visible to all tiers, locked items show upgrade prompt
const MAIN_NAV_ITEMS = [
  { id: "home", label: "Home", labelEs: "Inicio", icon: Home, path: "/", requiredTier: null, permissionKey: "home_dashboard" },
  // Sign-In Kiosk section — near top for quick iPad access
  {
    id: "signin-kiosk-section",
    label: "Sign-In Kiosk",
    labelEs: "Quiosco de Entrada",
    icon: QrCode,
    isExpandable: true,
    noAutoNavigate: true,
    requiredTier: "basic",
    subItems: [
      { id: "kiosk-scanner", label: "Kiosk Scanner", labelEs: "Escáner de Entrada", icon: QrCode, path: "/kiosk", requiredTier: "basic" },
      { id: "print-keychain-cards", label: "Print Keychain Cards", labelEs: "Imprimir Tarjetas", icon: Printer, path: "/print-keychain-cards", requiredTier: "basic", ownerOnly: true },
    ]
  },
  // Children expandable section
  { 
    id: "children-section", 
    label: "Children", 
    labelEs: "Niños", 
    icon: Baby, 
    isExpandable: true,
    subItems: [
      { id: "manage-children", label: "Enrolled Children", labelEs: "Niños Inscritos", icon: Users, path: "/children", requiredTier: "basic", featureCode: "child_management", permissionKey: "manage_children" },
      { id: "child-records", label: "Compliance Records", labelEs: "Registros de Cumplimiento", icon: ClipboardList, path: "/child-records", requiredTier: "premium", featureCode: "child_compliance_uploads", permissionKey: "child_records_docs" },
      { id: "bulk-upload", label: "Child Enrollment Bulk Upload", labelEs: "Carga Masiva de Inscripción", icon: Upload, path: "/children/bulk-upload", requiredTier: "premium", featureCode: "bulk_upload", ownerOnly: true, permissionKey: "bulk_upload_children" },
      { id: "form-builder", label: "Form Builder", labelEs: "Constructor de Formularios", icon: Wand2, path: "/form-builder", requiredTier: "premium", featureCode: "form_builder_hub", ownerOnly: true, permissionKey: "form_builder_hub" },
      { id: "records-bulk-upload", label: "Compliance Records Bulk Date Entry", labelEs: "Entrada Masiva de Fechas de Cumplimiento", icon: Table, path: "/children/records-bulk-upload", requiredTier: "premium", featureCode: "child_compliance_uploads", permissionKey: "records_bulk_upload" },
      { id: "attendance", label: "Attendance Report", labelEs: "Informe de Asistencia", icon: Calendar, path: "/attendance-report", requiredTier: "premium", featureCode: "attendance_reports", ownerOnly: true, permissionKey: "attendance_report" },
      { id: "daily-reports", label: "Daily Reports (Infant/Toddler)", labelEs: "Informes Diarios", icon: BarChart3, path: "/staff-reports", requiredTier: "premium", featureCode: "infant_toddler_reports", permissionKey: "daily_reports" },
      { id: "child-service-report", label: "Child Service Report", labelEs: "Informe de Servicio del Niño", icon: FileText, path: "/child-service-report-summary", requiredTier: "premium", ownerOnly: true, permissionKey: "compliance_dashboard" },
      { id: "emergency", label: "Emergency Contact", labelEs: "Contactos de Emergencia", icon: Phone, path: "/emergency-contacts", requiredTier: "basic", featureCode: "emergency_report", permissionKey: "emergency_contact" },
      { id: "field-trip-tags", label: "Field Trip Tags", labelEs: "Etiquetas de Excursión", icon: Tag, path: "/field-trip-tags", requiredTier: "basic", permissionKey: "field_trip_tags" },
    ]
  },
  // Tour & Ratio Planner — standalone top-level tab, directly below Children (moved out of the
  // Children submenu per owner request; route/permissions unchanged). Visibility (shown at all)
  // is decided EXCLUSIVELY by `tourPlannerAccess` -> `canSeeTourPlanner` (owner/co-owner or a
  // per-user grant), never the global permission matrix. requiredTier/featureCode (added) only
  // control the lock badge + upgrade modal, same as every other tier-gated nav item.
  { id: "tour-ratio-planner", label: "Tour & Ratio Planner", labelEs: "Planificador de Recorridos", icon: Calendar, path: "/tour-ratio-planner", requiredTier: "premium", featureCode: "tour_ratio_planner", tourPlannerAccess: true },
  // Staff expandable section
  { 
    id: "staff-section", 
    label: "Staff", 
    labelEs: "Personal", 
    icon: UserCog, 
    isExpandable: true,
    subItems: [
      { id: "staff-compliance", label: "Compliance Records & Training", labelEs: "Registros de Cumplimiento y Capacitación", icon: FileCheck, path: "/staff-records", requiredTier: "premium", featureCode: "staff_compliance_tracker", permissionKey: "staff_compliance_records" },
      { id: "staff-clearance-hub", label: "Clearance Hub", labelEs: "Centro de Habilitación de Personal", icon: ShieldCheck, path: "/staff/clearance-hub", requiredTier: "basic", permissionKey: "staff_clearance_hub" },
      { id: "professional-development-hub", label: "Professional Development Hub", labelEs: "Centro de Desarrollo Profesional", icon: BookOpen, path: "/staff/professional-development", requiredTier: "basic", permissionKey: "professional_development" },
      { id: "staff-assignment", label: "Daily Roster", labelEs: "Lista Diaria", icon: Calendar, path: "/staff-reports?tab=staff_assignment", requiredTier: "premium", featureCode: "staff_calendar", permissionKey: "staff_assignment" },
      { id: "staff-upload-center", label: "Upload Center", labelEs: "Centro de Carga", icon: Upload, path: "/staff-uploads", requiredTier: "premium", featureCode: "staff_compliance_tracker", permissionKey: "staff_upload_center" },
    ]
  },
  // Staff Administration — director-only
  {
    id: "staff-admin-section",
    label: "Staff Administration",
    labelEs: "Administración de Personal",
    icon: Settings,
    isExpandable: true,
    ownerStrictOnly: true,
    subItems: [
      { id: "manage-staff", label: "Manage Staff", labelEs: "Administrar Personal", icon: Users, path: "/users", requiredTier: "deluxe", featureCode: "user_management", ownerOnly: true, ownerStrictOnly: true, permissionKey: "manage_staff" },
      { id: "staff-evaluation", label: "Staff Evaluation", labelEs: "Evaluación de Personal", icon: ClipboardList, path: "/staff-reports?tab=staff_evaluation", requiredTier: "premium", ownerOnly: true, permissionKey: "staff_evaluation" },
      { id: "policy-acknowledgements", label: "Policy Acknowledgements", labelEs: "Reconocimientos de Políticas", icon: ScrollText, path: "/settings/policy-acknowledgements", requiredTier: "deluxe", ownerStrictOnly: true, permissionKey: "policy_acknowledgements" },
    ]
  },
  { id: "lesson-planning", label: "Lesson Planning Hub", labelEs: "Centro de Planificación", icon: BookOpen, path: "/lesson-planning", requiredTier: "premium", featureCode: "staff_compliance_tracker", permissionKey: "lesson_planning" },
  // Compliance expandable section
  { 
    id: "compliance-section", 
    label: "Compliance", 
    labelEs: "Cumplimiento", 
    icon: Shield, 
    isExpandable: true,
    ownerOnly: true,
    subItems: [
      { id: "compliance-dashboard", label: "Compliance Dashboard", labelEs: "Panel de Cumplimiento", icon: BarChart3, path: "/compliance-dashboard", requiredTier: "deluxe", featureCode: "compliance_dashboard", ownerOnly: true, permissionKey: "compliance_dashboard" },
      { id: "inspection-binder", label: "Inspection Binder", labelEs: "Carpeta de Inspección", icon: FolderOpen, path: "/compliance-dashboard?mode=inspection&scrollTo=binder", requiredTier: "deluxe", featureCode: "inspection_binder", isHighlighted: true, ownerOnly: true, permissionKey: "inspection_binder" },
      { id: "attendance-trends", label: "Attendance Trends & Analytics", labelEs: "Tendencias de Asistencia", icon: TrendingUp, path: "/attendance-trends", requiredTier: "premium", featureCode: "attendance_trends", ownerOnly: true, permissionKey: "attendance_trends" },
    ]
  },
  { 
    id: "settings-section", 
    label: "Settings", 
    labelEs: "Configuración", 
    icon: Settings, 
    isExpandable: true,
    ownerOnly: true,
    subItems: [
      { id: "backup", label: "Backup & Restore", labelEs: "Respaldo y Restauración", icon: Database, path: "/backup", requiredTier: "enterprise", featureCode: "full_backup", ownerOnly: true, permissionKey: "backup_restore" },
      { id: "security", label: "Security", labelEs: "Seguridad", icon: Shield, path: "/security", requiredTier: "enterprise", featureCode: "security_settings", ownerStrictOnly: true, permissionKey: "security" },
      { id: "email-activity", label: "Email Activity", labelEs: "Actividad de Correo", icon: Mail, path: "/settings/email-activity", requiredTier: null, ownerOnly: true, permissionKey: "email_activity" },
      { id: "owner-tools", label: "Owner Tools", labelEs: "Herramientas del Propietario", icon: Wrench, path: "/owner-tools", requiredTier: null, primaryOwnerOnly: true, ownerStrictOnly: true },
      { id: "customer", label: "Customer Portal", labelEs: "Portal de Clientes", icon: Building2, path: "/admin/customers", requiredTier: null, primaryOwnerOnly: true, ownerStrictOnly: true },
    ]
  },
  // Billing & Payments expandable section
  { 
    id: "billing-section", 
    label: "Billing & Payments", 
    labelEs: "Facturación y Pagos", 
    icon: CreditCard, 
    isExpandable: true,
    ownerOnly: true,
    billingManagerAccess: true,
    subItems: [
      { id: "payments", label: "Payments", labelEs: "Pagos", icon: CreditCard, path: "/billing/payments", requiredTier: "enterprise", featureCode: "invoicing", ownerOnly: true, permissionKey: "payments" },
      { id: "auto-invoicing", label: "Auto Invoicing", labelEs: "Facturación Automática", icon: FileText, path: "/billing/auto-invoicing", requiredTier: "enterprise", featureCode: "auto_invoicing", ownerOnly: true, permissionKey: "auto_invoicing" },
      { id: "payroll", label: "Payroll Hub", labelEs: "Centro de Nómina", icon: Building2, path: "/billing/payroll", requiredTier: "enterprise", featureCode: "payroll_hub", ownerOnly: true, permissionKey: "payroll_hub" },
    ]
  },
  // Revenue Hub — expandable section
  {
    id: "revenue-hub-section",
    label: "$$$ Revenue Hub",
    labelEs: "Centro de Ingresos $$$",
    icon: TrendingUp,
    isExpandable: true,
    ownerOnly: true,
    subItems: [
      { id: "family-comms-hub", label: "Family Communications Hub", labelEs: "Hub de Comunicaciones Familiares", icon: Megaphone, path: "/family-communications", requiredTier: "premium", featureCode: "family_communications_hub", permissionKey: "family_communications_hub" },
      { id: "revenue-parent-sms", label: "Parent SMS Communications", labelEs: "SMS para Padres", icon: MessageSquare, path: "/parent-communication", requiredTier: "premium", ownerOnly: true, permissionKey: "parent_sms_communications" },
      { id: "revenue-pickup-safety", label: "Pick Up Safety Alerts", labelEs: "Alertas de Seguridad", icon: ShieldCheck, path: "/pickup-safety", requiredTier: "premium", ownerOnly: true, permissionKey: "pickup_safety_alerts" },
    ]
  },
  // Parent Communication Center Email — standalone (no revenue stream)
  { id: "parent-comm-email", label: "Parent Communication Center Email", labelEs: "Centro de Comunicación Email", icon: FolderOpen, path: "/parent-communication-center", requiredTier: "premium", featureCode: "parent_communication_email", ownerStrictOnly: true },
  // Standalone items (not in expandable sections)
  { 
    id: "center-menu-section", 
    label: "Center Menus", 
    labelEs: "Menús del Centro", 
    icon: UtensilsCrossed, 
    isExpandable: true,
    subItems: [
      { id: "daily-menu-lunch", label: "Center Menu Lunch", labelEs: "Menú de Almuerzo", icon: UtensilsCrossed, path: "/daily-menu/lunch", requiredTier: "premium", featureCode: "menu_planner", permissionKey: "center_menu" },
      { id: "daily-menu-breakfast", label: "Center Menu Breakfast/Snack", labelEs: "Menú de Desayuno/Merienda", icon: UtensilsCrossed, path: "/daily-menu/breakfast-snack", requiredTier: "premium", featureCode: "menu_planner", permissionKey: "center_menu" },
    ]
  },
  { id: "inventory-shopping-list", label: "Menu, Inventory & Shopping List", labelEs: "Menú, Inventario y Lista de Compras", icon: UtensilsCrossed, path: "/inventory-shopping-list", requiredTier: "premium", featureCode: "inventory_shopping_list", permissionKey: "inventory_shopping", inventoryPerUserAccess: true },
  { id: "features-guide", label: "Features Guide", labelEs: "Guía de Funciones", icon: HelpCircle, path: "/features-guide", requiredTier: "deluxe", lockDuringTrial: true, hidden: true },
  {
    id: "cacfp-section",
    label: "Meal Compliance Tracker",
    labelEs: "Rastreador de Comidas",
    icon: Utensils,
    isExpandable: true,
    requiredTier: "deluxe",
    featureCode: "meal_compliance",
    subItems: [
      { id: "cacfp", label: "Meal Tracker", labelEs: "Rastreador de Comidas", icon: Utensils, path: "/cacfp", requiredTier: "deluxe", featureCode: "meal_compliance", permissionKey: "meal_compliance_tracker" },
      { id: "cacfp-forms", label: "CACFP Forms", labelEs: "Formularios CACFP", icon: FileText, path: "/cacfp/forms", requiredTier: "deluxe", featureCode: "meal_compliance", permissionKey: "meal_compliance_tracker" },
    ]
  },
];

export default function AppShell({ children, pageTitle, breadcrumb }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const { hasNewMessage, clearNewMessageFlag, triggerBeep } = useTeamMessages();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showNewMessageBadge, setShowNewMessageBadge] = useState(false);
  // Initialize expanded sections from sessionStorage (clears when the app/tab is closed)
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sidebarExpandedSections');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Tier gating state
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState(null);

  // Permission overrides from Permissions Manager
  const [permissionOverrides, setPermissionOverrides] = useState({});
  // Tier overrides from Tier Permissions Manager
  const [tierOverrides, setTierOverrides] = useState({});
  // Roster unread update notification (staff only)
  const [rosterHasUpdate, setRosterHasUpdate] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/owner/permissions`).catch(() => ({ data: {} })),
      axios.get(`${API}/owner/tier-permissions`).catch(() => ({ data: {} }))
    ]).then(([permRes, tierRes]) => {
      setPermissionOverrides(permRes.data?.permissions || {});
      setTierOverrides(tierRes.data?.tiers || {});
    });
  }, []);

  useEffect(() => {
    if (!user || user.role === 'owner' || user.role === 'co-owner') return;
    axios.get(`${API}/staff-roster/has-update`)
      .then(res => setRosterHasUpdate(res.data?.hasUpdate || false))
      .catch(() => {});
  }, [user]);

  // Check if the current user's role has permission for a feature key
  const PERMISSION_DEFAULTS = {
    'child_records_docs':  { 'co-owner': true, 'business-manager': false, 'staff': false },
    'records_bulk_upload': { 'co-owner': true, 'business-manager': false, 'staff': false },
    'form_builder_hub':    { 'co-owner': true, 'business-manager': false, 'staff': false },
    'email_activity':      { 'co-owner': false, 'business-manager': false, 'staff': false },
    'inventory_shopping':  { 'co-owner': true, 'business-manager': true, 'staff': false },
  };
  const hasRolePermission = (permissionKey) => {
    if (!permissionKey) return true; // No key = always visible
    if (user?.role === 'owner') return true; // Owner always sees everything
    const defaults = PERMISSION_DEFAULTS[permissionKey] || {};
    const rolePerms = permissionOverrides[permissionKey] || {};
    const effective = { ...defaults, ...rolePerms }; // stored overrides win
    if (Object.keys(effective).length === 0) return true; // Nothing defined = allow
    return effective[user?.role] !== false;
  };

  // Get current tier from subscription
  const { currentTier, isLoading: tierLoading } = useFeatureAccess('daily_signin'); // Use any feature to get tier
  
  // Persist expanded sections to sessionStorage (survives navigation, clears on app close)
  useEffect(() => {
    sessionStorage.setItem('sidebarExpandedSections', JSON.stringify(expandedSections));
  }, [expandedSections]);
  
  // Show notification badge when there's a new message
  useEffect(() => {
    if (hasNewMessage) {
      setShowNewMessageBadge(true);
      console.log("[AppShell] New message detected - showing badge");
    }
  }, [hasNewMessage]);

  const handleMessageClick = () => {
    setShowNewMessageBadge(false);
    clearNewMessageFlag();
    // Navigate directly to today's sign-in page where center messages are displayed
    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    navigate(`/signin/${formattedDate}`);
  };

  const isOwner = user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner' || user?.role?.toLowerCase() === 'business-manager';
  const isOwnerStrict = user?.role === 'owner'; // Owner only, not co-owner or business-manager
  const isBusinessManager = user?.role?.toLowerCase() === 'business-manager';
  const isPrimaryOwner = organization?.isOwnerAccount === true;
  // Tour & Ratio Planner visibility (shown in sidebar at all) is role-based only: owner/co-owner
  // always see it, or a per-user grant. Deliberately NOT using the global permissionOverrides
  // matrix (unsafe for per-user, per-org grants). Tier gating (lock badge/upgrade prompt) is
  // handled separately via requiredTier/featureCode on the nav item above + <FeatureGate> on the page.
  const canSeeTourPlanner = user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner' || user?.permissions?.canAccessTourPlanner === true;
  // Inventory nav visibility: normal role-based gating (hasRolePermission) OR a per-user grant —
  // same reasoning/pattern as Tour Planner above. A staff member granted canAccessInventory sees
  // the link without needing the 'inventory_shopping' role permission or a role change.
  const canSeeInventory = hasRolePermission('inventory_shopping') || user?.permissions?.canAccessInventory === true;

  // Tier order for comparison
  const TIER_ORDER = ['basic', 'premium', 'deluxe', 'enterprise', 'signature', 'trial'];
  
  // Check if user has access to a feature based on tier
  const hasFeatureAccess = (requiredTier, featureCode = null, lockDuringTrial = false) => {
    if (!requiredTier) return true;
    // Trial ended → lock all gated features until upgrade
    if (currentTier === 'expired') return false;
    // Lock during trial if explicitly flagged
    if (currentTier === 'trial' && lockDuringTrial) return false;
    if (currentTier === 'trial' || currentTier === 'signature') return true;
    // Apply tier override if one exists for this feature
    const effectiveTier = (featureCode && tierOverrides[featureCode]) ? tierOverrides[featureCode] : requiredTier;
    const currentIndex = TIER_ORDER.indexOf(currentTier?.toLowerCase() || 'signature');
    const requiredIndex = TIER_ORDER.indexOf(effectiveTier?.toLowerCase() || 'basic');
    return currentIndex >= requiredIndex;
  };

  // Handle clicking on a locked feature
  const handleLockedFeatureClick = (item) => {
    setLockedFeature({
      label: language === 'es' ? item.labelEs : item.label,
      requiredTier: item.requiredTier,
      featureCode: item.featureCode
    });
    setUpgradeModalOpen(true);
  };

  const handleLogout = () => {
    setExpandedSections([]);
    sessionStorage.removeItem('sidebarExpandedSections');
    logout();
    navigate("/login");
  };

  // Compliance badge — all pages except Dashboard (Dashboard handles its own)
  const [complianceBadge, setComplianceBadge] = useState(null);
  const fetchComplianceBadge = useCallback(async () => {
    try {
      const [visRes, scoreRes] = await Promise.all([
        axios.get(`${API}/organizations/compliance-visibility`),
        axios.get(`${API}/compliance/readiness-score`)
      ]);
      setComplianceBadge({
        score: Math.round((scoreRes.data?.score || 0) * 10) / 10,
        shared: visRes.data?.showComplianceToStaff || false
      });
    } catch { setComplianceBadge(null); }
  }, []);
  useEffect(() => { fetchComplianceBadge(); }, [fetchComplianceBadge, location.pathname]);
  useEffect(() => {
    const interval = setInterval(fetchComplianceBadge, 60000);
    return () => clearInterval(interval);
  }, [fetchComplianceBadge]);

  // Scroll direction arrow — global, all pages
  const [scrollDir, setScrollDir] = useState('down'); // 'up' | 'down'
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setShowScrollBtn(maxScroll > 100);
      setScrollDir(scrollY > 300 ? 'up' : 'down');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => {
    // Handle paths with query parameters (tabs)
    if (path.includes('?tab=')) {
      const [basePath, tabParam] = path.split('?tab=');
      const currentTab = new URLSearchParams(location.search).get('tab');
      // Exact match: both path and tab must match
      return location.pathname === basePath && currentTab === tabParam;
    }
    
    // Handle paths with query parameters (other)
    if (path.includes('?')) {
      const [basePath, query] = path.split('?');
      return location.pathname === basePath && location.search.includes(query);
    }
    
    // Home page - exact match only
    if (path === "/") {
      return location.pathname === "/" && !location.search;
    }
    
    // For base paths without query params (like /staff-reports),
    // only match if the current URL also has no tab parameter
    // This prevents /staff-reports from matching when URL is /staff-reports?tab=staff_assignment
    const currentTab = new URLSearchParams(location.search).get('tab');
    if (currentTab && location.pathname === path) {
      // URL has a tab param but path doesn't - not a match
      return false;
    }
    
    // All other paths - exact match only (no partial matching)
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    // Scroll to top when navigating
    window.scrollTo(0, 0);
    
    if (path.includes('?tab=')) {
      const [basePath, tabParam] = path.split('?tab=');
      const tabName = tabParam;
      
      // Navigate to the base path first, then trigger tab change
      if (basePath === '' || basePath === '/') {
        // Home page tabs - use custom event since HomePage manages its own state
        navigate('/');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('changeTab', { detail: tabName }));
        }, 100);
      } else {
        // Other pages with tabs (e.g., /staff-reports) - navigate with full path including query
        // The page component will read the tab from URL directly (single source of truth)
        navigate(path);
      }
    } else if (path === '/') {
      // When going to Home, reset to default calendar tab
      navigate('/');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'calendar' }));
      }, 100);
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const NavItem = ({ item, collapsed }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const label = language === 'es' ? item.labelEs : item.label;
    const isLocked = !hasFeatureAccess(item.requiredTier, item.featureCode, item.lockDuringTrial);

    const handleClick = () => {
      if (isLocked) {
        handleLockedFeatureClick(item);
      } else {
        handleNavigation(item.path);
      }
    };

    return (
      <button
        onClick={handleClick}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
          transition-all duration-200
          ${isLocked 
            ? "text-white/50 hover:bg-white/5"
            : active 
              ? "bg-white/20 text-white" 
              : "text-white/80 hover:bg-white/10 hover:text-white"
          }
          ${collapsed ? "justify-center" : ""}
        `}
      >
        <div className="relative">
          <Icon className={`w-5 h-5 flex-shrink-0 ${isLocked ? "text-white/40" : active ? "text-white" : "text-white/70"}`} />
          {isLocked && !collapsed && (
            <Lock className="absolute -top-1 -right-1 w-3 h-3 text-amber-400" />
          )}
        </div>
        {!collapsed && (
          <span className="flex-1 text-left">{label}</span>
        )}
        {isLocked && !collapsed && (
          <Lock className="w-4 h-4 text-amber-400/80" />
        )}
      </button>
    );
  };

  // Handle clicking on expandable section header - navigate to section path or first sub-item
  const handleSectionClick = (item) => {
    // Toggle the section (expand/collapse)
    toggleSection(item.id);
    
    // If marked as expand-only, never auto-navigate
    if (item.noAutoNavigate) return;

    // If the section has its own path, navigate there
    if (item.path) {
      handleNavigation(item.path);
      return;
    }
    
    // Otherwise navigate to first accessible sub-item
    const firstAccessibleSubItem = item.subItems?.find(sub => {
      const hasTierAccess = hasFeatureAccess(sub.requiredTier, sub.featureCode);
      const hasRoleAccess = (!sub.primaryOwnerOnly || isPrimaryOwner) && (!sub.ownerOnly || isOwner) && (!sub.ownerStrictOnly || isOwnerStrict);
      return hasTierAccess && hasRoleAccess;
    });
    
    if (firstAccessibleSubItem) {
      handleNavigation(firstAccessibleSubItem.path);
    }
  };

  // Expandable section component
  const ExpandableNavSection = ({ item, collapsed }) => {
    const Icon = item.icon;
    const isExpanded = expandedSections.includes(item.id);
    const label = language === 'es' ? item.labelEs : item.label;
    
    // Check if any sub-item is active
    const hasActiveSubItem = item.subItems?.some(sub => isActive(sub.path));

    if (collapsed) {
      // When collapsed, show just the icon that expands on hover/click
      return (
        <div className="relative group">
          <button
            onClick={() => handleSectionClick(item)}
            className={`
              w-full flex items-center justify-center px-3 py-2.5 rounded-xl text-sm font-medium
              transition-all duration-200
              ${hasActiveSubItem ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"}
            `}
          >
            <Icon className={`w-5 h-5 ${hasActiveSubItem ? "text-white" : "text-white/70"}`} />
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <button
          onClick={() => handleSectionClick(item)}
          className={`
            w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium
            transition-all duration-200
            ${hasActiveSubItem ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"}
          `}
        >
          <div className="flex items-center gap-3">
            <Icon className={`w-5 h-5 ${hasActiveSubItem ? "text-white" : "text-white/70"}`} />
            <span>{label}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-white/70" />
          ) : (
            <ChevronRight className="w-4 h-4 text-white/70" />
          )}
        </button>
        
        {isExpanded && (
          <div className="ml-4 pl-3 border-l border-white/20 space-y-1">
            {item.subItems
              ?.filter(sub => {
                if (sub.primaryOwnerOnly && !isPrimaryOwner) return false;
                if (sub.ownerOnly && !isOwner) return false;
                if (sub.ownerStrictOnly && !isOwnerStrict) return false;
                return hasRolePermission(sub.permissionKey);
              })
              .map(subItem => {
                const SubIcon = subItem.icon;
                const subActive = isActive(subItem.path);
                const subLabel = language === 'es' ? subItem.labelEs : subItem.label;
                const isHighlighted = subItem.isHighlighted;
                const isSubLocked = !hasFeatureAccess(subItem.requiredTier, subItem.featureCode);
                
                const handleSubClick = () => {
                  if (isSubLocked) {
                    handleLockedFeatureClick(subItem);
                  } else {
                    if (subItem.id === 'staff-assignment') setRosterHasUpdate(false);
                    handleNavigation(subItem.path);
                  }
                };
                
                return (
                  <button
                    key={subItem.id}
                    onClick={handleSubClick}
                    data-testid={`nav-item-${subItem.id}`}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                      transition-all duration-200
                      ${isSubLocked
                        ? "text-white/40 hover:bg-white/5"
                        : isHighlighted 
                          ? "text-white font-medium tracking-wide bg-white/10 hover:bg-white/20"
                          : subActive 
                            ? "bg-white/20 text-white font-medium" 
                            : "text-white/70 hover:bg-white/10 hover:text-white"
                      }
                    `}
                  >
                    <SubIcon className={`w-4 h-4 ${isSubLocked ? "text-white/40" : isHighlighted ? "text-white" : subActive ? "text-white" : "text-white/60"}`} />
                    <span className="flex-1 text-left whitespace-pre-line">{subLabel}</span>
                    {subItem.id === 'staff-assignment' && rosterHasUpdate && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="relative flex h-2.5 w-2.5 ml-1">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500"></span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">
                            Your roster was recently updated
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {isSubLocked && (
                      <Lock className="w-3.5 h-3.5 text-amber-400/80" />
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-surface-bg text-ink-main">
      <PaymentStatusBanner />
      <div className="flex">
        {/* Desktop Sidebar */}
        <aside 
          className={`
            hidden lg:flex flex-col min-h-screen bg-brand-primary text-white
            transition-all duration-300 ease-in-out print:!hidden
            ${sidebarOpen ? "w-64" : "w-20"}
          `}
        >
          {/* Logo Section */}
          <div className="p-4 border-b border-white/15">
            <div className={`flex items-center gap-3 ${!sidebarOpen && "justify-center"}`}>
              <img
                src="/simple-sign-in-logo.png"
                className="h-12 w-12 rounded-xl bg-white p-1 object-contain"
                alt="ToDays Kidz"
              />
              {sidebarOpen && (
                <div className="leading-tight">
                  <div className="font-semibold text-lg">ToDays Kidz</div>
                  <div className="text-sm text-white/80">Simple Sign-In</div>
                </div>
              )}
            </div>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {MAIN_NAV_ITEMS
              .filter(item => !item.hidden)
              .filter(item => {
                // Tour & Ratio Planner nav visibility is decided EXCLUSIVELY by canSeeTourPlanner —
                // it must never fall through to hasRolePermission (permissive for an undefined key,
                // or could be unsafely re-gated later via the global permissionOverrides matrix).
                if (item.tourPlannerAccess) return canSeeTourPlanner;
                // Inventory nav visibility adds a per-user override on top of the normal role check.
                if (item.inventoryPerUserAccess) return canSeeInventory;
                const passes = (!item.primaryOwnerOnly || isPrimaryOwner) &&
                  (!item.ownerOnly || isOwner) &&
                  (!item.ownerStrictOnly || isOwnerStrict) &&
                  hasRolePermission(item.permissionKey);
                if (!passes) return false;
                // For expandable sections, hide if no sub-items are visible for this role
                if (item.isExpandable && item.subItems) {
                  const visibleSubs = item.subItems.filter(sub =>
                    (!sub.primaryOwnerOnly || isPrimaryOwner) &&
                    (!sub.ownerOnly || isOwner) &&
                    (!sub.ownerStrictOnly || isOwnerStrict) &&
                    hasRolePermission(sub.permissionKey)
                  );
                  return visibleSubs.length > 0;
                }
                return true;
              })
              .map((item) => (
                item.isExpandable ? (
                  <ExpandableNavSection key={item.id} item={item} collapsed={!sidebarOpen} />
                ) : (
                  <NavItem key={item.id} item={item} collapsed={!sidebarOpen} />
                )
              ))}

            {/* Features Guide quick-access drawer — sits right after last nav item */}
            {(currentTier === 'deluxe' || currentTier === 'enterprise' || currentTier === 'signature') && (
              <button
                onClick={() => setGuideOpen(true)}
                data-testid="floating-features-guide-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                title="Features Guide"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm">Features Guide</span>}
              </button>
            )}
          </nav>

          {/* Footer — Collapse toggle only */}
          <div className="p-3 border-t border-white/15">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${!sidebarOpen ? "rotate-180" : ""}`} />
              {sidebarOpen && <span className="text-sm">Collapse</span>}
            </button>
            
            {sidebarOpen && (
              <p className="text-xs text-white/50 text-center mt-4">
                © {new Date().getFullYear()} ToDays Kidz
              </p>
            )}
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside 
          className={`
            fixed inset-y-0 left-0 z-50 w-64 bg-brand-primary text-white
            transform transition-transform duration-300 ease-in-out lg:hidden
            ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/15">
            <div className="flex items-center gap-3">
              <img
                src="/simple-sign-in-logo.png"
                className="h-10 w-10 rounded-xl bg-white p-1 object-contain"
                alt="ToDays Kidz"
              />
              <div className="leading-tight">
                <div className="font-semibold">ToDays Kidz</div>
                <div className="text-xs text-white/80">Simple Sign-In</div>
              </div>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {MAIN_NAV_ITEMS
              .filter(item => item.tourPlannerAccess ? canSeeTourPlanner : item.inventoryPerUserAccess ? canSeeInventory : (!item.ownerOnly || isOwner) && (!item.ownerStrictOnly || isOwnerStrict) && hasRolePermission(item.permissionKey))
              .filter(item => !item.hidden)
              .map((item) => (
                item.isExpandable ? (
                  <ExpandableNavSection key={item.id} item={item} collapsed={false} />
                ) : (
                  <NavItem key={item.id} item={item} collapsed={false} />
                )
              ))}

            {/* Features Guide — mobile */}
            {(currentTier === 'deluxe' || currentTier === 'enterprise' || currentTier === 'signature') && (
              <button
                onClick={() => { setMobileMenuOpen(false); setGuideOpen(true); }}
                data-testid="mobile-features-guide-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors mt-1"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">Features Guide</span>
              </button>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Trial Ended Banner — reassures user their data is safe */}
          {!tierLoading && currentTier === 'expired' && (
            <div className="bg-amber-50 border-b border-amber-300 px-4 lg:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap print:!hidden">
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <Lock className="w-4 h-4 flex-shrink-0 text-amber-600" />
                <span>
                  <span className="font-semibold">Your trial has ended.</span>{' '}
                  Your children, staff, sign-in history, and compliance records are saved — upgrade to resume sign-ins and editing.
                </span>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors flex-shrink-0"
              >
                Upgrade Now
              </button>
            </div>
          )}

          {/* Top Header Bar */}
          <header className="sticky top-0 z-30 bg-white border-b border-line print:!hidden">
            <div className="px-4 lg:px-6 py-3 flex items-center justify-between flex-wrap gap-y-2">
              <div className="flex items-center gap-4">
                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="p-2 rounded-lg hover:bg-surface-bg lg:hidden"
                >
                  <Menu className="w-5 h-5 text-ink-main" />
                </button>
                
                <div>
                  {breadcrumb && (
                    <div className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md inline-block">
                      {organization?.name || 'ToDays Kidz Childcare Center'}
                    </div>
                  )}
                  <h1 className="text-xl font-semibold text-ink-main">
                    {pageTitle || "Dashboard"}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                {/* Compliance badge — visible on all pages except Dashboard (Dashboard shows its own) */}
                {complianceBadge && location.pathname !== '/' && (isOwner || complianceBadge.shared) && (
                  <span
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md cursor-pointer hover:from-emerald-400 hover:to-green-500 transition-all select-none"
                    onClick={() => navigate('/compliance-dashboard')}
                    data-testid="appshell-compliance-badge"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{complianceBadge.score}% Compliant</span>
                  </span>
                )}
                {/* Hard Refresh Button */}
                <button
                  onClick={() => {
                    if ('caches' in window) {
                      caches.keys().then(names => {
                        names.forEach(name => caches.delete(name));
                      });
                    }
                    localStorage.removeItem('cachedData');
                    sessionStorage.clear();
                    window.location.reload(true);
                  }}
                  className="relative p-2 rounded-lg hover:bg-red-100 transition-colors"
                  title="Having issues? Try clicking here to clear cached data and reload the app"
                  data-testid="hard-refresh-btn"
                >
                  <RefreshCw className="w-5 h-5 text-red-500 hover:text-red-600" />
                </button>

                {/* New Message Notification Bell with Tooltip */}
                <div className="relative group">
                  <button
                    onClick={handleMessageClick}
                    className="relative p-2 rounded-lg hover:bg-amber-100 transition-colors"
                    data-testid="header-message-bell"
                  >
                    <Bell className={`w-5 h-5 ${showNewMessageBadge ? 'text-amber-600 animate-bounce' : 'text-gray-500'}`} />
                    {showNewMessageBadge && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-bold">!</span>
                      </span>
                    )}
                  </button>
                  {/* Hover Tooltip */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                    Navigate to Daily Sign-In & Center Message Area
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                </div>
                
                {/* User Profile Section */}
                <div className="text-right">
                  <div className="hidden sm:flex items-center gap-2 justify-end">
                    <span className="text-sm font-medium text-ink-main">
                      {user?.name || user?.email}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary capitalize">
                      {getRoleDisplayName(user?.role)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 justify-end">
                    {/* Edit Profile Button */}
                    <button
                      onClick={() => navigate('/profile')}
                      className="text-xs text-brand-primary hover:text-brand-secondary transition-colors font-medium"
                    >
                      Edit Profile
                    </button>
                    <span className="text-ink-muted text-xs">|</span>
                    {/* Language Toggle */}
                    <button
                      onClick={toggleLanguage}
                      className="flex items-center gap-1 text-xs text-ink-muted hover:text-brand-primary transition-colors"
                    >
                      <Globe className="w-3 h-3" />
                      <span>{language === 'en' ? 'ES' : 'EN'}</span>
                    </button>
                    <span className="text-ink-muted text-xs">|</span>
                    {/* Logout Button - More visible */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 text-base font-semibold text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-200 border border-red-200 hover:border-red-500"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Holiday Banner - Full Width Below Header */}
          <div className="px-4 lg:px-6 py-2 bg-white border-b border-line space-y-2 print:!hidden">
            <HolidayCard />
            <StaffCalendar currentUser={user} />
          </div>

          {/* Page Content */}
          <div className="p-4 lg:p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Global scroll direction arrow — no background, just arrow */}
      {showScrollBtn && (
        <button
          onClick={() => {
            if (scrollDir === 'down') {
              window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          className="fixed bottom-6 right-6 z-50 p-2 transition-all duration-300 hover:scale-125 print:hidden"
          title={scrollDir === 'down' ? 'Scroll to bottom' : 'Scroll to top'}
          data-testid="global-scroll-btn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#64748b"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              transform: scrollDir === 'up' ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease'
            }}
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <polyline points="19 12 12 19 5 12" />
          </svg>
        </button>
      )}

      {guideOpen && <FeaturesGuideDrawer onClose={() => setGuideOpen(false)} />}

      {/* Upgrade Required Modal */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-amber-50">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <DialogTitle className="text-center text-xl">
              Upgrade Required
            </DialogTitle>
            <DialogDescription className="text-center">
              <span className="font-semibold text-gray-900">{lockedFeature?.label}</span> requires the{' '}
              <span className="font-bold text-brand-primary capitalize">
                {lockedFeature?.requiredTier}
              </span>{' '}
              plan or higher.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Current vs Required Tier */}
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="text-center px-4 py-2 bg-gray-100 rounded-lg">
                <div className="text-gray-500">Current Plan</div>
                <div className="font-semibold capitalize">{currentTier || 'Basic'}</div>
              </div>
              <Sparkles className="w-5 h-5 text-amber-500" />
              <div className="text-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-amber-600">Required</div>
                <div className="font-semibold capitalize text-amber-700">{lockedFeature?.requiredTier}</div>
              </div>
            </div>

            {/* Tier Info */}
            {lockedFeature?.requiredTier && TIER_INFO[lockedFeature.requiredTier] && (
              <div className="text-center text-sm text-gray-600">
                Starting at <span className="font-bold text-gray-900">{TIER_INFO[lockedFeature.requiredTier].price}</span>
                {' '}• Up to {TIER_INFO[lockedFeature.requiredTier].maxChildren} children
              </div>
            )}

            {/* Reassurance Note — your data is safe */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                Your data is safe
              </div>
              <p className="text-xs leading-relaxed">
                All your children, staff, sign-in history, and compliance records are still here exactly as you left them. Choose a plan and pick up right where you stopped.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setUpgradeModalOpen(false)}
              >
                Maybe Later
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => {
                  setUpgradeModalOpen(false);
                  navigate('/pricing');
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                View Plans
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
